import { Box, GridLegacy, Stack, Typography } from '@mui/material';
import { GridLegacySize } from '@mui/material/GridLegacy';

export type FieldLabelLayout =
  | 'none'
  | 'floating'
  | 'horizontal'
  | 'vertical'
  | { label: GridLegacySize; field: GridLegacySize };

export type FieldLabelProps = {
  htmlFor?: React.LabelHTMLAttributes<HTMLLabelElement>['htmlFor'];
  labelProps?: Omit<
    React.LabelHTMLAttributes<HTMLLabelElement>,
    'htmlFor' | 'children'
  >;
  layout?: FieldLabelLayout;
  required?: boolean;
  label: React.ReactNode;
  endAdornment?: React.ReactNode;
  /** Field to be labeled */
  children?: React.ReactNode;
};

const RenderedLabel = ({
  htmlFor,
  labelProps,
  required,
  label,
  endAdornment,
}: Omit<FieldLabelProps, 'layout' | 'children'>) => (
  <label style={{ display: 'block' }} htmlFor={htmlFor} {...labelProps}>
    {/* For now adornment will always be to the left of label even if it wraps. It is
    a question for UX whether it should rather be part of Typography and wrap with it */}
    <Stack component="span" direction="row" spacing={1} alignItems="center">
      <Typography
        sx={{
          fontSize: theme => theme.typography.pxToRem(15),
          fontWeight: 500,
        }}
      >
        {label}
        {required && (
          <Box
            component="span"
            sx={{
              color: theme => theme.palette.error.main,
              marginLeft: theme => theme.spacing(0.25),
            }}
          >
            *
          </Box>
        )}
      </Typography>
      {endAdornment}
    </Stack>
  </label>
);

export const FieldLabel = ({
  htmlFor,
  labelProps,
  layout = 'vertical',
  required,
  label,
  endAdornment,
  children,
}: FieldLabelProps) => {
  if (layout === 'none' || layout === 'floating') {
    return <>{children}</>;
  }

  if (layout === 'horizontal' || layout === 'vertical') {
    return (
      <Stack
        direction={layout === 'horizontal' ? 'row' : 'column'}
        spacing={layout === 'horizontal' ? 2 : 0}
      >
        <RenderedLabel
          htmlFor={htmlFor}
          labelProps={labelProps}
          required={required}
          label={label}
          endAdornment={endAdornment}
        />
        {children}
      </Stack>
    );
  }

  return (
    <GridLegacy container item xs={12} spacing={2}>
      <GridLegacy item xs={layout.label} display="flex" alignItems="center">
        <RenderedLabel
          htmlFor={htmlFor}
          labelProps={labelProps}
          required={required}
          label={label}
          endAdornment={endAdornment}
        />
      </GridLegacy>
      <GridLegacy item xs={layout.field}>
        {children}
      </GridLegacy>
    </GridLegacy>
  );
};
